import * as XLSX from 'xlsx';
import { Receipt } from '../../types';

// Selection interface based on the JSON structure
interface Selection {
    comment: string;
    item_id: number;
    quantity: number;
    item_name: string;
    modifiers: Array<{
        option_id: number;
        option_name: string;
        modifier_id: number;
        modifier_name: string;
        modifier_price: number;
    }>;
    base_price: number;
    clientIndex: number;
    total_price: number;
    selection_id: string;
    selection_groups: Array<{
        selected_items: Array<{
            item_id: number;
            quantity: number;
            item_name: string;
        }>;
        selection_group_id: number;
    }>;
}

// Helper function to format currency for Excel (as number)
const formatCurrency = (value: number): number => {
    return Math.round(value);
};

// Helper function to format product name (like RenderSelections does)
const formatProductName = (selection: Selection): string => {
    let productName = `${selection.quantity}x ${selection.item_name}`;
    
    // Add selection groups items
    if (selection.selection_groups && selection.selection_groups.length > 0) {
        const groupItems = selection.selection_groups
            .flatMap(sg => sg.selected_items.map(sgi => sgi.item_name))
            .filter(name => name);
        
        if (groupItems.length > 0) {
            productName += ` (${groupItems.join(', ')})`;
        }
    }
    
    // Add modifiers
    if (selection.modifiers && selection.modifiers.length > 0) {
        const modifierNames = selection.modifiers.map(mod => mod.option_name).filter(name => name);
        if (modifierNames.length > 0) {
            if (selection.selection_groups && selection.selection_groups.length > 0) {
                productName += `, ${modifierNames.join(', ')}`;
            } else {
                productName += ` (${modifierNames.join(', ')})`;
            }
        }
    }
    
    // Add comment if exists
    if (selection.comment) {
        productName += ` - Nota: ${selection.comment}`;
    }
    
    return productName;
};

// Transform receipts data for "Recibos" sheet
const transformReceiptsData = (receipts: Receipt[]) => {
    return receipts.map(receipt => ({
        'ID Recibo': receipt.receipt_id || '',
        'Número Recibo': receipt.receipt_number || '',
        'Fecha': receipt.local_timestamp || receipt.timestamp || '',
        'Hora Inicio': receipt.time_started || '',
        'Mesa': receipt.mesa_num || '',
        'Usuario': receipt.user_names.join(', '),
        'Tipo Pedido': receipt.pedido_type_id || '',
        'Cliente': receipt.client_name || '',
        'App Externa': receipt.third_party_provider_name || '',
        'Subtotal': formatCurrency(receipt.subtotal),
        'Descuento': formatCurrency(receipt.discount_amount || 0),
        'Cupón': formatCurrency(receipt.coupon_amount || 0),
        'Propina': formatCurrency(receipt.tip_payed || 0),
        'Total Pagado': formatCurrency(receipt.total_payed),
        'Total Debido': formatCurrency(receipt.total_due),
        'Cambio': formatCurrency(receipt.receipt_change || 0),
        'Pagado Completo': receipt.payed_full ? 'Sí' : 'No',
        'Impreso': receipt.is_printed ? 'Sí' : 'No',
        'Cancelado': receipt.is_cancelled ? 'Sí' : 'No',
        'Reembolsado': receipt.is_refunded ? 'Sí' : 'No',
        'Sesión ID': receipt.session_id || '',
        'ID Turno': receipt.shift_id || '',
        'Turno': receipt.shift_name || 'N/A',
    }));
};

// Transform selections data for "Por Producto" sheet with prorated discounts and coupons
const transformSelectionsData = (receipts: Receipt[]) => {
    const selectionsData: any[] = [];
    
    receipts.forEach(receipt => {
        if (!receipt.selections || receipt.selections.length === 0) return;
        
        const selections: Selection[] = receipt.selections;
        
        // Calculate total of all selections for proration
        const selectionsTotal = selections.reduce((sum, sel) => sum + sel.total_price, 0);
        
        // Get receipt-level discount and coupon amounts
        const totalDiscount = receipt.discount_amount || 0;
        const totalCoupon = receipt.coupon_amount || 0;
        
        selections.forEach((selection: Selection) => {
            // Calculate proration factor for this selection
            const prorationFactor = selectionsTotal > 0 ? selection.total_price / selectionsTotal : 0;
            
            // Prorate discount and coupon based on selection's share of total
            const proratedDiscount = totalDiscount * prorationFactor;
            const proratedCoupon = totalCoupon * prorationFactor;
            
            // Calculate net sale (Precio Venta - Descuento - Cupón)
            const precioVenta = selection.total_price;
            const ventaNeta = precioVenta - proratedDiscount - proratedCoupon;
            
            // Prorate tip as well (assuming tip is distributed across items)
            const proratedTip = (receipt.tip_payed || 0) * prorationFactor;
            
            selectionsData.push({
                'ID Recibo': receipt.receipt_id || '',
                'Producto': formatProductName(selection),
                'ID Producto': selection.item_id,
                'Cantidad': selection.quantity,
                'Precio Base': formatCurrency(selection.base_price),
                'Precio Venta': formatCurrency(precioVenta),
                'Descuento': formatCurrency(proratedDiscount),
                'Cupón': formatCurrency(proratedCoupon),
                'Venta Neta': formatCurrency(ventaNeta),
                'Propina': formatCurrency(proratedTip),
                'Usuarios': receipt.user_names.join(', '),
                'Type ID': receipt.pedido_type_id || '',
                'App Externa': receipt.third_party_provider_name || '',
                'Fecha': receipt.local_timestamp || receipt.timestamp || '',
                'Mesa': receipt.mesa_num || '',
                'Número Recibo': receipt.receipt_number || '',
                'Cliente': receipt.client_name || '',
                'Sesión ID': receipt.session_id || '',
                'ID Turno': receipt.shift_id || '',
                'Turno': receipt.shift_name || 'N/A',
            });
        });
    });
    
    return selectionsData;
};

// Main export function
export const exportReceiptsToExcel = (
    receipts: Receipt[],
    fileName?: string
) => {
    try {
        // Transform data for both sheets
        const receiptsData = transformReceiptsData(receipts);
        const selectionsData = transformSelectionsData(receipts);
        
        // Create workbook
        const wb = XLSX.utils.book_new();
        
        // Create "Recibos" sheet
        const wsRecibos = XLSX.utils.json_to_sheet(receiptsData);
        
        // Set column widths for Recibos sheet
        const recibosColWidths = [
            { wch: 10 },  // ID Recibo
            { wch: 15 },  // Número Recibo
            { wch: 20 },  // Fecha
            { wch: 20 },  // Hora Inicio
            { wch: 10 },  // Mesa
            { wch: 20 },  // Usuario
            { wch: 12 },  // Tipo Pedido
            { wch: 20 },  // Cliente
            { wch: 20 },  // App Externa
            { wch: 12 },  // Subtotal
            { wch: 12 },  // Descuento
            { wch: 12 },  // Cupón
            { wch: 12 },  // Propina
            { wch: 12 },  // Total Pagado
            { wch: 12 },  // Total Debido
            { wch: 12 },  // Cambio
            { wch: 15 },  // Pagado Completo
            { wch: 10 },  // Impreso
            { wch: 10 },  // Cancelado
            { wch: 12 },  // Reembolsado
            { wch: 12 },  // Sesión ID
            { wch: 12 },  // ID Turno
            { wch: 15 },  // Turno
        ];
        wsRecibos['!cols'] = recibosColWidths;
        
        // Add Recibos sheet to workbook
        XLSX.utils.book_append_sheet(wb, wsRecibos, 'Recibos');
        
        // Create "Por Producto" sheet
        const wsSelections = XLSX.utils.json_to_sheet(selectionsData);
        
        // Set column widths for Selections sheet
        const selectionsColWidths = [
            { wch: 10 },  // ID Recibo
            { wch: 50 },  // Producto (wider for full description)
            { wch: 12 },  // ID Producto
            { wch: 10 },  // Cantidad
            { wch: 12 },  // Precio Base
            { wch: 12 },  // Precio Venta
            { wch: 12 },  // Descuento
            { wch: 12 },  // Cupón
            { wch: 12 },  // Venta Neta
            { wch: 12 },  // Propina
            { wch: 20 },  // Usuarios
            { wch: 10 },  // Type ID
            { wch: 20 },  // App Externa
            { wch: 20 },  // Fecha
            { wch: 10 },  // Mesa
            { wch: 15 },  // Número Recibo
            { wch: 20 },  // Cliente
            { wch: 12 },  // Sesión ID
            { wch: 12 },  // ID Turno
            { wch: 15 },  // Turno
        ];
        wsSelections['!cols'] = selectionsColWidths;
        
        // Add Selections sheet to workbook
        XLSX.utils.book_append_sheet(wb, wsSelections, 'Por Producto');
        
        // Generate filename
        const timestamp = new Date().toISOString().split('T')[0];
        const finalFileName = fileName || `Recibos_${timestamp}.xlsx`;
        
        // Write file
        XLSX.writeFile(wb, finalFileName);
        
        console.log(`Excel exported successfully: ${finalFileName}`);
    } catch (error) {
        console.error('Error generating Excel file:', error);
        throw error;
    }
};

// Export filtered receipts by date range
export const exportFilteredReceipts = (
    receipts: Receipt[],
    startDate?: string,
    endDate?: string,
    fileName?: string
) => {
    let filteredReceipts = receipts;
    
    // Apply date filters if provided
    if (startDate || endDate) {
        filteredReceipts = receipts.filter(receipt => {
            const receiptDate = receipt.local_timestamp || receipt.timestamp;
            if (!receiptDate) return false;
            
            const date = new Date(receiptDate);
            
            if (startDate && new Date(startDate) > date) return false;
            if (endDate && new Date(endDate) < date) return false;
            
            return true;
        });
    }
    
    const timestamp = new Date().toISOString().split('T')[0];
    const dateRange = startDate && endDate 
        ? `${startDate}_a_${endDate}`
        : timestamp;
    
    const finalFileName = fileName || `Recibos_${dateRange}.xlsx`;
    
    exportReceiptsToExcel(filteredReceipts, finalFileName);
};
